export const PRODUCT_IMAGE =
  'https://cdn.magicpatterns.com/uploads/tnmM5qCmVKdnin4e96gA2n/ChatGPT_Image_7_sept_2026,_17_11_11.png'

export type Problem = {
  title: string
  detail: string
  icon: 'clock' | 'help' | 'folders' | 'battery'
}

export const problems: Problem[] = [
  {
    title: 'Endless therapy planning',
    icon: 'clock',
    detail: 'Activities, goals, materials and session ideas — rebuilt week after week.',
  },
  {
    title: 'Never knowing what to use next',
    icon: 'help',
    detail: 'You know the client and the goal, but still have to find the right resource.',
  },
  {
    title: 'Therapy resources everywhere',
    icon: 'folders',
    detail: 'PDFs, saved links, worksheets and notes scattered across too many places.',
  },
  {
    title: 'Preparation fatigue',
    icon: 'battery',
    detail: 'Searching. Printing. Adapting. Organizing. Then doing it all again.',
  },
]

/** The six real printable clinical tools provided by the client. */
export const printables = [
  {
    label: 'Communication Repair Strategies',
    image: 'https://cdn.magicpatterns.com/uploads/6KQufPEtA6eFrMo6S1yfrU/p10-10.png',
  },
  {
    label: 'Describe It! — Vocabulary & Language',
    image: 'https://cdn.magicpatterns.com/uploads/fepriEG87d3qfQ5ATtJsya/p12-12.png',
  },
  {
    label: 'What Happens Next? — Sequencing',
    image: 'https://cdn.magicpatterns.com/uploads/tCsU17AvSBd9uRgXRY8t17/p14-14.png',
  },
  {
    label: 'Find the Difference — Describe & Compare',
    image: 'https://cdn.magicpatterns.com/uploads/3M2eC3p7KzvvgzPC1Xjwmz/p16-16.png',
  },
  {
    label: 'Following Directions Challenge',
    image: 'https://cdn.magicpatterns.com/uploads/wUVGPJENsFdKJwV1MNkNuV/p20-20(1).png',
  },
  {
    label: 'Everyday Memory & Message Tracker',
    image: 'https://cdn.magicpatterns.com/uploads/hXGgWKSRL2RTYZXStzNhvH/p34-34.png',
  },
]

export const masterGuideChapters = [
  {
    number: '01',
    title: 'Clinical assessment & reasoning',
    detail:
      'Move from referral, history and communication sampling to clear clinical priorities and treatment decisions.',
  },
  {
    number: '02',
    title: 'Ready-to-adapt goal libraries',
    detail:
      '105 ready-to-adapt goals across pediatric speech and language, adult communication, AAC, motor speech and functional participation.',
  },
  {
    number: '03',
    title: 'Speech & language intervention',
    detail:
      'Practical guidance for articulation, phonology, receptive and expressive language, narrative, discourse and functional communication.',
  },
  {
    number: '04',
    title: 'Therapy activity libraries',
    detail:
      '56 ready-to-adapt therapy activities covering speech, language, vocabulary, grammar, narrative, functional communication, adult rehabilitation and AAC.',
  },
  {
    number: '05',
    title: '32 complete therapy blueprints',
    detail:
      'Ready-to-run 30-, 45- and 60-minute session structures with clear ways to adapt support, challenge, engagement, fatigue and functional practice.',
  },
  {
    number: '06',
    title: 'Progress & documentation',
    detail:
      'Practical systems for cueing, prompting, progress monitoring, clinical adaptation and documentation that supports the next treatment decision.',
  },
]

export const masterGuideStats = [
  { value: '170', label: 'Pages' },
  { value: '13', label: 'Clinical chapters' },
  { value: '105', label: 'Ready-to-adapt goals' },
  { value: '56', label: 'Therapy activities' },
  { value: '32', label: 'Therapy blueprints' },
]

export type Bonus = {
  number: string
  title: string
  detail: string
  icon: 'documentation' | 'scenarios' | 'caregiver' | 'pediatric' | 'neuro'
}

export const bonuses: Bonus[] = [
  {
    number: 'Bonus 1',
    title: 'Speech Therapy Documentation Vault™',
    detail: 'Documentation resources to make notes and progress tracking faster and organized.',
    icon: 'documentation',
  },
  {
    number: 'Bonus 2',
    title: 'Functional Communication Scenario Bank™',
    detail: '100+ real-life scenarios for meaningful pediatric and adult practice.',
    icon: 'scenarios',
  },
  {
    number: 'Bonus 3',
    title: 'Caregiver & Home Carryover Playbook™',
    detail: 'Strategies to extend therapy goals into everyday routines and home practice.',
    icon: 'caregiver',
  },
  {
    number: 'Bonus 4',
    title: 'Pediatric Speech & Language Activity Vault™',
    detail: '100+ adaptable activities for speech, language, articulation, phonology and AAC.',
    icon: 'pediatric',
  },
  {
    number: 'Bonus 5',
    title: 'Adult Neuro Communication Toolkit™',
    detail: 'Clinical resources for adult neurogenic and cognitive-communication needs.',
    icon: 'neuro',
  },
]

export type Benefit = {
  title: string
  detail: string
  icon: 'hours' | 'next' | 'organized' | 'ages' | 'therapy'
}

export const benefits: Benefit[] = [
  {
    title: 'Save hours of preparation',
    detail: 'Spend less time searching, building and organizing resources.',
    icon: 'hours',
  },
  {
    title: 'Know what to use next',
    detail: 'Move into sessions with organized professional support already available.',
    icon: 'next',
  },
  {
    title: 'Keep your materials organized',
    detail: 'Stop relying on scattered PDFs, links, notes and old folders.',
    icon: 'organized',
  },
  {
    title: 'Support pediatric + adult practice',
    detail: 'Use one professional system across different areas of your caseload.',
    icon: 'ages',
  },
  {
    title: 'Spend more time on actual therapy',
    detail: 'Reduce repetitive preparation around the clinical work you already know.',
    icon: 'therapy',
  },
]

export const steps = [
  { number: '1', title: 'Open', detail: 'Access your organized professional resource system.' },
  { number: '2', title: 'Choose', detail: 'Find what fits the client, session or goal.' },
  { number: '3', title: 'Use', detail: 'Print it, reference it and move into the session.' },
]

/** Sample layout content — to be replaced with verified customer feedback. */
export const testimonials = [
  {
    name: 'Emily R.',
    role: 'Speech-Language Pathologist',
    location: 'Austin, Texas',
    quote:
      'I was spending too much time pulling materials together before sessions. Having everything organized in one place makes preparation feel much more manageable.',
  },
  {
    name: 'Rachel M.',
    role: 'SLP',
    location: 'Toronto, Canada',
    quote:
      'What I like most is being able to open the system, find what fits the session and move on instead of searching through different folders and resources.',
  },
  {
    name: 'Lauren H.',
    role: 'Speech Therapist',
    location: 'Manchester, UK',
    quote:
      'It gives me a much clearer starting point when I’m planning. I’m not constantly wondering what resource I should prepare next.',
  },
  {
    name: 'Megan T.',
    role: 'Speech-Language Pathologist',
    location: 'Brisbane, Australia',
    quote:
      'I work across different needs, so having pediatric and adult resources organized within one system is incredibly practical.',
  },
]

export const faqs = [
  {
    question: 'How is the Speech Therapy Clinical Toolkit™ delivered?',
    answer:
      'Digitally. After checkout you receive instant access to every file — no shipping and no waiting.',
  },
  {
    question: 'Who is this made for?',
    answer:
      'Practicing Speech Therapists and Speech-Language Pathologists in private practice, clinics and school settings.',
  },
  {
    question: 'Does it support both pediatric and adult practice?',
    answer:
      'Yes. The resources are organized across pediatric and adult communication needs so you can pull what fits your caseload.',
  },
  {
    question: 'Are the clinical tools printable?',
    answer:
      'Yes. Everything is print-ready. Print a page once or as often as you need for your own practice.',
  },
  {
    question: 'Can I adapt the resources to my own clinical approach?',
    answer:
      'They are practical support for your clinical judgement — you decide how, when and with whom to use each resource.',
  },
  {
    question: 'When do I get access?',
    answer: 'Immediately after purchase. Your access details arrive by email within a few minutes.',
  },
  {
    question: 'What devices can I use to access the files?',
    answer:
      'Any device that opens a PDF — phone, tablet, laptop or desktop. No app or software installation needed.',
  },
  {
    question: 'What if it isn’t right for my practice?',
    answer:
      'You are covered by a 7-day money-back guarantee. If it does not fit your professional needs, request a refund.',
  },
]

export const purchaseNotifications = [
  { name: 'Sarah', location: 'Texas' },
  { name: 'Emily', location: 'Ontario' },
  { name: 'Jessica', location: 'California' },
  { name: 'Lauren', location: 'London' },
  { name: 'Megan', location: 'Queensland' },
  { name: 'Rachel', location: 'Dublin' },
  { name: 'Ashley', location: 'Florida' },
  { name: 'Chloe', location: 'Manchester' },
  { name: 'Danielle', location: 'Alberta' },
  { name: 'Hannah', location: 'Auckland' },
]
